import React, { useEffect, useMemo, useState } from 'react';
import PeriodSelect from '../common/PeriodSelect';
import EditorDrawer from './EditorDrawer';
import useStore from '../../store/useStore';
import { reportsAPI } from '../../services/api';
import { buildCatalogue } from '../report-editor/reportModel';
import '../../styles/DashboardFilters.css';

const MAX_FILTERS = 8;

/**
 * The fields viewers can filter the dashboard by. Each report widget is
 * filtered where its data links to the field; others say they are not.
 */
const FilterFields = ({ filters, onChange }) => {
  const companyId = useStore((state) => state.currentCompany?.id);
  const [cubes, setCubes] = useState(null);
  useEffect(() => {
    let live = true;
    reportsAPI.getMeta(companyId).then((res) => { if (live) setCubes(res?.data?.cubes || []); }).catch(() => { if (live) setCubes([]); });
    return () => { live = false; };
  }, [companyId]);
  const catalogue = useMemo(() => buildCatalogue(cubes || []), [cubes]);
  const used = new Set(filters.map((f) => f.member));
  const groups = catalogue.cubes
    .filter((c) => !c.isMetric)
    .map((c) => ({ cube: c, fields: c.dimensions.filter((d) => d.kind === 'dimension' && !used.has(d.name)) }))
    .filter((g) => g.fields.length > 0);
  const fieldTitle = (member) => {
    const f = catalogue.fields.get(member);
    return f ? `${catalogue.byCube.get(f.cube)?.title || f.cube} · ${f.label}` : member;
  };
  const add = (member) => {
    const f = catalogue.fields.get(member);
    onChange([...filters, { id: Math.random().toString(36).slice(2, 8), member, label: f?.label || member.split('.').pop() }]);
  };

  return (
    <fieldset className="inspector-field dashboard-filter-fields">
      <legend>Filters</legend>
      <small className="inspector-hint">Viewers can narrow the dashboard's reports by these fields. A report that has no link to a field is left as it is and says so.</small>
      {filters.map((f, i) => (
        <div key={f.id} className="dashboard-filter-field">
          <input aria-label={`Name of the filter on ${fieldTitle(f.member)}`} value={f.label} maxLength={60}
            onChange={(e) => onChange(filters.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
          <small title={f.member}>{fieldTitle(f.member)}</small>
          <button type="button" className="btn-secondary-small" aria-label={`Remove the filter ${f.label}`} onClick={() => onChange(filters.filter((_, j) => j !== i))}>Remove</button>
        </div>
      ))}
      {filters.length < MAX_FILTERS && (
        <select aria-label="Add a filter" value="" disabled={!cubes} onChange={(e) => { if (e.target.value) add(e.target.value); }}>
          <option value="">{cubes ? 'Add a filter…' : 'Loading fields…'}</option>
          {groups.map((g) => (
            <optgroup key={g.cube.name} label={g.cube.title}>
              {g.fields.map((d) => <option key={d.name} value={d.name}>{d.label}</option>)}
            </optgroup>
          ))}
        </select>
      )}
    </fieldset>
  );
};

/**
 * Settings for the dashboard being edited: name, default period, when live
 * data counts as stale, whether it opens first, and deleting it.
 */
const DashboardSettingsPanel = ({ dashboard, onSave, onDelete, onClose, saving = false }) => {
  const [form, setForm] = useState(() => initial(dashboard));
  const [confirmDelete, setConfirmDelete] = useState('');
  useEffect(() => { setForm(initial(dashboard)); }, [dashboard?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = (e) => {
    e.preventDefault();
    if (form.name.trim().length < 3) return;
    onSave({
      name: form.name.trim(),
      defaultDateRange: form.defaultDateRange,
      staleThresholdMinutes: form.stale === '' ? null : Number(form.stale),
      isDefault: form.isDefault,
      filters: form.filters.map((f) => ({ ...f, label: f.label.trim() || f.member.split('.').pop() })),
    });
  };

  return (
    <EditorDrawer
      title="Dashboard settings"
      subtitle={dashboard?.name}
      onClose={onClose}
      labelId="dashboard-settings-title"
      footer={(
        <div className="inspector-footer">
          <span className="inspector-spacer" />
          <button type="submit" form="dashboard-settings-form" className="btn-primary-small" disabled={saving || form.name.trim().length < 3}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      )}
    >
      <form id="dashboard-settings-form" className="inspector-form" onSubmit={submit}>
        <label className="inspector-field">
          <span>Name</span>
          <input value={form.name} minLength={3} required onChange={(e) => update({ name: e.target.value })} />
        </label>
        <div className="inspector-field">
          <span>Period when the dashboard opens</span>
          <PeriodSelect ariaLabel="Period when the dashboard opens" value={form.defaultDateRange} onChange={(v) => update({ defaultDateRange: v })}
            emptyLabel="Each report's own period" ranges="calendar-financial" />
        </div>
        <small className="inspector-hint">Reports and statements that follow the dashboard period use this. Live widgets are not affected.</small>
        <FilterFields filters={form.filters} onChange={(filters) => update({ filters })} />
        <label className="inspector-field">
          <span>Mark live data stale after (minutes)</span>
          <input type="number" min={1} max={1440} value={form.stale} placeholder={`Company default (${dashboard?.company_stale_minutes ?? 5})`} onChange={(e) => update({ stale: e.target.value })} />
        </label>
        <label className="inspector-check">
          <input type="checkbox" checked={form.isDefault} onChange={(e) => update({ isDefault: e.target.checked })} />
          <span>Open this dashboard first for everyone in the company</span>
        </label>
      </form>

      <section className="inspector-danger" aria-labelledby="danger-heading">
        <h3 id="danger-heading">Delete dashboard</h3>
        <p>Removes the dashboard and its widgets. Saved reports, KPIs and data are kept.</p>
        <label className="inspector-field">
          <span>Type the dashboard name to confirm</span>
          <input value={confirmDelete} onChange={(e) => setConfirmDelete(e.target.value)} aria-label="Type the dashboard name to confirm deletion" />
        </label>
        <button type="button" className="btn-danger-small" disabled={confirmDelete !== dashboard?.name} onClick={onDelete}>Delete dashboard</button>
      </section>
    </EditorDrawer>
  );
};

function initial(d) {
  return {
    name: d?.name || '',
    defaultDateRange: d?.default_date_range || '',
    stale: d?.stale_threshold_override ?? '',
    isDefault: Boolean(d?.is_default),
    filters: Array.isArray(d?.filters) ? d.filters : [],
  };
}

export default DashboardSettingsPanel;
